<template>
    <div>
        <div class="pb-2 d-flex justify-space-between" style="gap: 8px;">
            <v-hover v-slot="{ hover }">
                <v-btn
                    :outlined="hover ? false : true"
                    :elevation="hover ? 8 : 0"
                    :loading="loading"
                    :disabled="loading"
                    dark
                    small
                    color="#ff530d"
                    @click="openCreateModal"
                >
                    <font-awesome-icon icon="fas fa-upload" class="mr-2"/>
                    Upload Template
                </v-btn
                >
            </v-hover>
            
            <v-hover v-slot="{ hover }">
                <v-btn
                    :outlined="hover ? false : true"
                    :elevation="hover ? 8 : 0"
                    :loading="loading"
                    :disabled="loading"
                    dark
                    small
                    color="#30527e"
                    @click="downloadBlankTemplate"
                    >
                    <font-awesome-icon icon="fas fa-file-download" class="mr-2"/>
                    Download Blank Template
                </v-btn
                >
            </v-hover>

        </div>

        <v-data-table
            :headers="headers"
            :items="templates"
            :server-items-length="totalTemplates"
            :loading="loading"
            :options.sync="options"
            @click:row="openDetails"
            :footer-props="{
                itemsPerPageOptions: [5, 25, 50],
            }"
            loading-text="Getting slide themes..."
        >
            <template v-slot:item.thumbnail="{ item }">
                <div>
                    <v-img
                        v-if="item.thumbnail"
                        class="my-1"
                        style="border-radius: 5px;"
                        width="160px"
                        contain
                        :src="item.thumbnail"
                        alt="Template thumbnail"
                    />
                </div>
            </template>
            <template v-slot:item.name="{ item }">
                <div class="template-col">
                    <div>
                        <v-img
                            class="my-1"
                            style="border-radius: 5px;"
                            width="160px"
                            contain
                            v-if="item.thumbnail"
                            :src="item.thumbnail"
                            alt="Template thumbnail"
                        />
                    </div>
                    <div>
                        <div><strong>{{ item.name }}</strong></div>
                        <div>{{ item.label }}</div>
                        <div>
                            <custom-tooltip :tooltipProps="{ bottom: true }" style="display: inline-block;">
                                <template v-slot:activator>
                                    <v-chip
                                        @click="openKeywordsModal(item)"
                                        class="ma-1"
                                        small
                                        color="primary"
                                    >
                                        <font-awesome-icon icon="far fa-tags" />
                                    </v-chip>
                                </template>

                                <template v-slot:content>
                                    <span>Add/Remove Tags</span>
                                </template>
                            </custom-tooltip>
                            
                            <v-chip
                                v-for="(keyword, index) in item.keywords"
                                class="ma-1"
                                label
                                small
                            >
                                {{ keyword }}
                            </v-chip>
                        </div>
                    </div>
                </div>
            </template>

            <template v-slot:item.createDate="{ item }">
                <div>
                    {{ item.createDate | moment('l') }}
                </div>
            </template>
            <template v-slot:item.updateDate="{ item }">
                <div>
                    {{ item.updateDate | moment('l') }}
                </div>
            </template>
            <template v-slot:item.duration="{ item }">
                <div>
                    {{ item.duration | durationTimeStamp }}
                </div>
            </template>

            <template v-slot:item.url="{ item }">
                <div>
                    <font-awesome-icon
                        style="color: #00c853; font-size: 1.2rem"
                        v-if="item.url"
                        icon="fa-regular fa-circle-check"
                    ></font-awesome-icon>
                    <font-awesome-icon
                        style="color: #fc676b; font-size: 1.2rem"
                        v-else
                        icon="fa-regular fa-circle-x"
                    ></font-awesome-icon>
                </div>
            </template>

            <template v-slot:item.actions="{ item }">
                <div class="action-list">
                    <custom-tooltip :tooltipProps="{ bottom: true }">
                        <template v-slot:activator>
                            <span>
                                <font-awesome-icon
                                    class="trash"
                                    icon="fa-solid fa-download"
                                    style="font-size: 1rem"
                                    @click.stop="download(item)"
                                />
                            </span>
                        </template>

                        <template v-slot:content>
                            <span>Download</span>
                        </template>
                    </custom-tooltip>

                    <custom-tooltip :tooltipProps="{ bottom: true }">
                        <template v-slot:activator>
                            <span>
                                <font-awesome-icon
                                    class="trash"
                                    icon="fa-regular fa-trash-can"
                                    style="font-size: 1rem"
                                    @click.stop="openDeleteModal(item)"
                                />
                            </span>
                        </template>

                        <template v-slot:content>
                            <span>Delete</span>
                        </template>
                    </custom-tooltip>
                </div>
            </template>
        </v-data-table>

        <v-dialog v-model="createModal" max-width=" 500px">
            <v-card class="p-3">
                <v-btn fab x-small class="close p-0" @click="() => { createModal = false; resetNewTemplate(); }" :elevation="0">
                    <font-awesome-icon icon="fa-solid fa-times" class="fa-lg" />
                </v-btn>
                <h5>Create Template</h5>
                <v-form ref="newTemplateForm" v-model="createModalForm">
                    <v-row>
                        <v-col>
                            <v-text-field
                                v-model="newTemplate.label"
                                required
                                outlined
                                prepend-icon="fas fa-images"
                                label="Label"
                            />
                            <v-file-input 
                                label="PSD" 
                                required
                                show-size
                                outlined
                                :persistent-hint="true"
                                :rules="psdRules"
                                hint="1920x1080 Photoshop PSD. Less than 30MB in size"
                                :accept="psdMimetypes"
                                v-model=newTemplate.psdFile>
                            </v-file-input>
                            <v-file-input label="Thumbnail"
                                required
                                show-size
                                outlined
                                :persistent-hint="true"
                                :rules="thumbRules"
                                hint="20-50% of the PSD's size. Less than 5MB in size."
                                :accept="thumbnailMimetypes"
                                v-model="newTemplate.thumbnail">
                            </v-file-input>
                        </v-col>
                    </v-row>
                    <div class="text-right">
                        <v-btn
                            :dark="!loading"
                            color="#0d3d60"
                            :loading="loading"
                            @click="submitCreate"
                        >
                            Create
                        </v-btn>
                    </div>
                </v-form>
            </v-card>
        </v-dialog>

        <v-dialog v-model="keywordsModal" max-width=" 500px">
            <v-card class="p-3">
                <div v-if="selectedTemplate != null">
                    <v-card-title class="modal-header">
                        <v-img
                            v-if="selectedTemplate.thumbnail"
                            height="50px"
                            class="my-0 template-thumbail"
                            style="border-radius: 5px;"
                            contain
                            :src="selectedTemplate.thumbnail"
                            alt="Template thumbnail"
                        />
                        <h5>Add/Remove keywords</h5>
                    </v-card-title>
                    <div>
                        <v-combobox
                            v-model="templateKeywords"
                            chips
                            :items="[]"
                            multiple
                        />
                    </div>
                    <div class="text-right">
                        <v-btn
                            color="error"
                            :disabled="templateKeywords == selectedTemplate.keywords"
                            @click="submitKeywordChanges(selectedTemplate.externalId)"
                        >
                        Save Changes
                        </v-btn>
                    </div>
                </div>
            </v-card>
        </v-dialog>
        <v-dialog v-model="deleteModal" max-width=" 500px">
            <v-card class="p-3">
                <div v-if="selectedTemplate != null">
                    <v-card-title class="flex">
                        <h5>Delete Template?</h5>
                    </v-card-title>
                    <p>This will delete a slide template. This cannot be undone.</p>
                    <div class="p-2 bg-gray-200">
                        <v-img
                        class="my-0"
                        style="border-radius: 5px;"
                        contain
                        v-if="selectedTemplate.thumbnail"
                        :src="selectedTemplate.thumbnail"
                        alt="Template thumbnail"
                    />
                    </div>

                    <div class="text-right">
                        <v-btn
                            color="error"
                            :loading="loading"
                            :disabled="loading"
                            @click="deleteTemplate(selectedTemplate.externalId)"
                            >Delete</v-btn
                        >
                    </div>
                </div>
            </v-card>
        </v-dialog>
    </div>
</template>
<script>
import CustomTooltip from '@/components/ui/CustomTooltip.vue';
// import { creatomateRenderStatuses, templateDynamicElements, tributeTemplateStatuses } from '@/constants.js';
import { debounceV2 } from '@/utilities/debounce.js';
import { mapActions } from 'vuex';
import BlobService from '@/services/blob.service';
import { difference } from 'lodash';
const maxSize = 31457280; // ~30 MB
const maxThumbSize = 5242880; // ~5 MB
const errorMessage = 'Total image size should be less than 30MB.';
export default {
    data() {
        return {
            detailsModal: false,
            selectedTemplate: null,
            templates: [],
            totalTemplates: 0,
            options: {
                itemsPerPage: 25,
                page: 1,
            },
            newTemplate: {
                url: '',
                name: '',
                thumbnail: null,
                psdFile: null,
                label: null,
                keywords: []
            },
            loading: true,
            createModal: false,
            createModalForm: false,
            keywordsModal: false,
            templateKeywords: [],
            deleteModal: false,
            headers: [
                { text: 'Template', value: 'name', sortable: false },
                { text: 'Actions', value: 'actions', align: 'center', sortable: false },
            ],
            psdMimetypes: 'image/vnd.adobe.photoshop, application/x-photoshop, application/photoshop, application/psd, image/psd, image/x-photoshop',
            thumbnailMimetypes: 'image/png, image/jpeg, image/bmp, image/webp',
            psdRules: [
                value => {
                    if (value?.size > maxSize) {
                        this.showSnackbar({ message: 'PSD file is too large; Max size is 30MB.', color: 'red' });
                    }
                    return !value || value.size < maxSize || errorMessage
                }
            ],
            thumbRules: [
                value => {
                    if (value?.size > maxThumbSize) {
                        this.showSnackbar({ message: 'Thumbnail is too large, should be less than 5MB.', color: 'red' });
                    }
                    return !value || value.size < maxThumbSize || 'Thumbnail size should be less than 5MB.'
                }
            ],
        }
    },
    components: { CustomTooltip },
    props: ['search'],
    watch: {
        search: debounceV2(function () {
            this.getTemplates();
        }, 500),
        options: {
            handler() {
                if (!this.loading) {
                    this.getTemplates();
                }
            },
        },
    },
    methods: {
        ...mapActions(['showSnackbar', 'block']),
        deleteTemplate(templateId) {
            this.loading = true;
            this.axiosInstance
                .delete(`Editor/assets/${templateId}`)
                .then(respsone => {
                    this.showSnackbar({ message: 'Template deleted' });
                    this.deleteModal = false;
                    this.loading = false;
                    this.selectedTemplate = null;
                    this.getTemplates();
                })
                .catch(error => {
                    console.log(error, 'error');
                    this.showSnackbar({ message: 'Error deleting template', color: 'red' });
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        openDeleteModal(item) {
            this.selectedTemplate = item;
            this.deleteModal = true;
        },
        download(item) {
            if (item.psdFile?.length) {
                const link = document.createElement('a');
                link.href = item.psdFile;
                link.download = `${item.name || item.label}.psd`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                this.showSnackbar({ message: 'Download started' });
            } else {
                this.showSnackbar({ message: 'This template is missing a PSD', color: 'red' });
            }
        },
        downloadBlankTemplate() {
            const link = document.createElement('a');
            const origin = location.origin;
            link.href = `${origin}/imgly/assets/templates/template-1920x1080.psd`;
            link.download = `template-1920x1080.psd`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },
        openCreateModal() {
            this.createModal = true;
        },
        closeDetails() {
            this.selectedTemplate = null;
            this.titleText = '';
            this.editingTitle = false;
            this.detailsModal = false;
            this.detailStep = 1;
        },
        openDetails(item) {
            this.selectedTemplate = item;
            this.titleText = this.selectedTemplate.title;
            this.detailsModal = true;
        },
        getSortByValue(value) {
            switch (value) {
                case 'title':
                    return 'Title';
                default:
                    console.warn('No cases found in switch statement.');
                    return '';
            }
        },
        getTemplates() {
            this.loading = true;

            const { sortBy, sortDesc, page, itemsPerPage } = this.options;

            var params = {
                pageSize: itemsPerPage,
                pageNumber: page ? page - 1 : 1,
                sortBy: sortBy && sortBy[0] ? this.getSortByValue(sortBy[0]) : null || null,
                sortAsc: sortBy && sortBy[0] ? !sortDesc[0] : null || null,
                search: this.search,
            };

            this.axiosInstance
                .get(`Editor/assets/slide-template`, { params: params })
                .then(res => {
                    this.templates = res.data.assets;
                    console.log(res.data.assets, 'res.data.assets');
                    this.totalTemplates = res.data.total;
                })
                .catch(error => {
                    console.log(error, 'error');
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        resetNewTemplate() {
            this.newTemplate = {
                url: '',
                name: '',
                thumbnail: null,
                psdFile: null,
                label: null,
                keywords: []
            }
        },
        openKeywordsModal(template) {
            this.selectedTemplate = template;
            this.keywordsModal = true;
            this.templateKeywords = template.keywords;
        },
        async submitKeywordChanges() {
            // get a dif of added and removed keywords
            const removed = difference(this.selectedTemplate.keywords, this.templateKeywords);
            const added = difference(this.templateKeywords, this.selectedTemplate.keywords);
            console.log(added, removed);
            if (added.length || removed.length) {
                const index = this.templates.findIndex((template) => {
                    return template.externalId === this.selectedTemplate.externalId;
                });
                try {
                    if (added.length) {
                        const { data: addedResult } = await this.axiosInstance.post(`Editor/assets/keywords/add/${this.selectedTemplate.externalId}`, {
                            keywords: added
                        });
                        Object.assign(this.templates[index], addedResult);
                    }
                    if (removed.length) {
                        const { data: removedResult } = await this.axiosInstance.put(`Editor/assets/keywords/remove/${this.selectedTemplate.externalId}`, {
                            keywords: removed
                        });
                        Object.assign(this.templates[index], removedResult);
                    }
                    this.showSnackbar({ message: 'Keyword changes saved' });
                } catch (error) {
                    console.error(error);
                    this.showSnackbar({ message: 'Keyword changes failed to save', color: 'red' });
                }
            }

            // Make calls to add and remove as needed
            this.templateKeywords = null;
            this.selectedTemplate = false;
            this.keywordsModal = false;
        },
        createAxiosInstance() {
            this.axiosInstance = this.axios.create({
                headers: { Authorization: `Bearer ${this.token}` },
                baseURL: process.env.VUE_APP_API,
            });
        },
        async setAuthToken() {
            const response = await this.$auth.getIdTokenClaims();
            this.token = response.__raw;
        },
        refreshTemplate(data) {
            const index = this.templates.findIndex(x => x.id === data.id);

            if (index !== -1) {
                this.templates.splice(index, 1, {
                    ...this.templates[index],
                    duration: data.duration,
                    status: data.status,
                    title: data.title,
                    updateDate: data.updateDate,
                    url: data.url,
                });
            }
        },
        async submitCreate(event) {
            if (!this.canCreateTemplate || !this.createModalForm) {
                this.showSnackbar({ message: 'Incomplete template form', color: 'red' });
                return false;
            }
            this.block(true);
            this.newTemplate.name = this.newTemplate.psdFile.name;
            // Get PSD SAS info
            const { data: psdResult } = await this.axiosInstance.get(`Editor/assets/slide-template/psd/sas/${this.newTemplate.psdFile.name}`);
            // Get thumbnail SAS info
            const { data: thumbnailResult } = await this.axiosInstance.get(`Editor/assets/slide-template/thumbnail/sas/${this.newTemplate.thumbnail.name}`);
            // Upload files
            await BlobService.upload(psdResult.sas, this.newTemplate.psdFile);
            await BlobService.upload(thumbnailResult.sas, this.newTemplate.thumbnail);
            //  Create Template Item
            const {data: createResult, status } = await this.axiosInstance.post(`Editor/assets/slide-template`, [{
                name: this.newTemplate.name,
                url: '',
                psdFile: psdResult.sas.split('?')[0],
                thumbnail: thumbnailResult.sas.split('?')[0],
                label: this.newTemplate.label
            }])
            .catch((err) => {
                console.error(err);
                this.showSnackbar({ message: 'There was an error while creating template', color: 'red' });
                this.block(false);
            });
            if (status === 200) {
                this.resetNewTemplate();
                this.templates.unshift(createResult[0]);
                this.totalTemplates++;
                this.showSnackbar({ message: 'Template created updated' });
                this.createModal = false;
            } else {
                this.showSnackbar({ message: 'Could not create template', color: 'red' });
            }
            this.block(false);
        },
    },
    computed: {
        canCreateTemplate(newVal, oldVal) {
            if (!this.newTemplate.label?.length) {
                return false;
            }
            if (!this.newTemplate.psdFile) {
                return false;
            }
            if (!this.newTemplate?.thumbnail) {
                return false;
            }
            return true;
        }
    },
    async created() {
        await this.setAuthToken();
        this.createAxiosInstance();
        this.getTemplates();
    },
};
</script>
<style lang="scss" scoped>
.template-col {
    display: flex;
    flex-direction: row;
    gap: 10px;
    align-items: center;
}

.list-item:hover {
    background-color: rgba(0, 0, 0, 0.04);
}
.modal-header {
    display: flex;
    justify-content: space-between;
    flex-direction: row;
    align-items: center;

    & > * {
        flex: 1;
    }
    .template-thumbail {
        flex: 0;
        flex-basis: 25%;
    }
}
.close {
    position: absolute;
    right: 4px;
    top: 4px;
}
</style>
